import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { projectApi } from "../../api/projectApi";
import { useAuth } from "../../context/AuthContext";

const CDN = "https://r2-proxy.seohoon0707.workers.dev";

const T = {
  bg:        "#f8fafc",
  card:      "#ffffff",
  border:    "#e2e8f0",
  text:      "#0f172a",
  textMuted: "#64748b",
  accent:    "#0ea5e9",
  danger:    "#ef4444",
  radius:    "12px",
};

// ── 마크다운 에디터 툴바 ──
const TOOLBAR = [
  { label:"H1",  wrap:["# ",""],       block:true  },
  { label:"H2",  wrap:["## ",""],      block:true  },
  { label:"H3",  wrap:["### ",""],     block:true  },
  { label:"B",   wrap:["**","**"],     block:false },
  { label:"I",   wrap:["*","*"],       block:false },
  { label:"~~",  wrap:["~~","~~"],     block:false },
  { label:"`",   wrap:["`","`"],       block:false },
  { label:"```", wrap:["```\n","\n```"],block:true },
  { label:"—", sep:true },
  { label:"링크", wrap:["[","](url)"], block:false },
  { label:"인용", wrap:["\n> ",""],    block:true  },
  { label:"목록", wrap:["\n- ",""],    block:true  },
  { label:"번호", wrap:["\n1. ",""],   block:true  },
  { label:"HR", wrap:["\n---\n",""],  block:true  },
];

// ── TechTagInput ──
const TechTagInput = ({ tags, onChange }) => {
  const [input, setInput] = useState("");
  const isComposing = useRef(false);

  const addTag = useCallback((value) => {
    const trimmed = (value ?? input).trim();
    if (trimmed && !tags.includes(trimmed) && tags.length < 15) {
      onChange([...tags, trimmed]);
    }
    setInput("");
  }, [input, tags, onChange]);

  const handleKey = (e) => {
    if (isComposing.current) return;
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag();
    } else if (e.key === "Backspace" && !input && tags.length) {
      onChange(tags.slice(0, -1));
    }
  };

  return (
    <div style={{ marginBottom:"24px" }}>
      <label style={{ fontSize:"13px", fontWeight:700, color:T.text, textTransform:"uppercase", letterSpacing:"0.1em", display:"block", marginBottom:"8px" }}>사용 기술 (Tech Stack)</label>
      <div style={{ display:"flex", flexWrap:"wrap", gap:"8px", padding:"10px", background:"#fff", border:`1px solid ${T.border}`, borderRadius:"10px" }}>
        {tags.map((tag) => (
          <span key={tag} style={{ background:"#f1f5f9", color:"#475569", border:`1px solid ${T.border}`, borderRadius:"6px", padding:"4px 10px", fontSize:"12px", fontWeight:600, display:"flex", alignItems:"center", gap:"6px" }}>
            {tag}
            <button type="button" onClick={() => onChange(tags.filter(t => t !== tag))} style={{ border:"none", background:"none", color:"#94a3b8", cursor:"pointer" }}>×</button>
          </span>
        ))}
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          onCompositionStart={() => { isComposing.current = true; }}
          onCompositionEnd={(e) => { isComposing.current = false; if (e.data === "\n" || e.data === ",") addTag(); }}
          placeholder="예: React, Docker..."
          style={{ flex:1, border:"none", background:"transparent", color:T.text, outline:"none", fontSize:"13px" }}
        />
      </div>
    </div>
  );
};

const ProjectWritePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading } = useAuth();

  const [title,    setTitle]    = useState("");
  const [period,   setPeriod]   = useState("");
  const [summary,  setSummary]  = useState("");
  const [role,     setRole]     = useState("");
  const [tech,     setTech]     = useState([]);
  const [category, setCategory] = useState(["dev"]);
  const [github,   setGithub]   = useState("");
  const [demo,     setDemo]     = useState("");
  const [content,  setContent]  = useState("");
  const [uploadedImages, setUploadedImages] = useState([]);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const textareaRef = useRef();
  const isEditMode  = !!id;

  useEffect(() => {
    if (!authLoading && !isAdmin) navigate("/projects", { replace: true });
  }, [isAdmin, authLoading, navigate]);

  useEffect(() => {
    if (!isEditMode || authLoading || !isAdmin) return;
    projectApi.getForEdit(id).then(({ data }) => {
      setTitle(data.title || "");
      setPeriod(data.period || "");
      setSummary(data.summary || "");
      setRole(data.role || "");
      setTech(Array.isArray(data.tech) ? data.tech : JSON.parse(data.tech || "[]"));
      setCategory(Array.isArray(data.category) ? data.category : [data.category || "dev"]);
      setGithub(data.github_url || "");
      setDemo(data.demo_url || "");
      setContent(data.content || "");
      setThumbnailUrl(data.thumbnail_url || null);
      const keys = Array.isArray(data.image_keys) ? data.image_keys : JSON.parse(data.image_keys || "[]");
      setUploadedImages(keys.map(k => ({ key: k, url: `${CDN}/project/original/${k}.webp`, thumbnailUrl: `${CDN}/project/thumb/${k}.webp` })));
    });
  }, [id, isEditMode, authLoading, isAdmin]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith("image/"));
    if (!files.length) return;
    setSubmitting(true);
    try {
      for (const file of files) {
        const { data } = await projectApi.uploadImage(file);
        setUploadedImages(prev => [...prev, data]);
      }
    } finally { setSubmitting(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return alert("제목과 내용은 필수입니다.");
    setSubmitting(true);
    const payload = { title, period, summary, role, tech, category, githubUrl: github, demoUrl: demo, content, thumbnailUrl, imageKeys: uploadedImages.map(i => i.key) };
    try {
      if (isEditMode) await projectApi.update(id, payload);
      else await projectApi.create(payload);
      navigate("/projects");
    } catch {
      alert("프로젝트 저장에 실패했습니다.");
    } finally { setSubmitting(false); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => alert("URL이 복사되었습니다."));
  };

  const insertToContent = (url) => {
    const ta = textareaRef.current;
    const { selectionStart: s, selectionEnd: e } = ta;
    const inserted = `\n![image](${url})\n`;
    const nextContent = content.slice(0, s) + inserted + content.slice(e);
    setContent(nextContent);
    setTimeout(() => {
      ta.focus();
      const pos = s + inserted.length;
      ta.selectionStart = ta.selectionEnd = pos;
    }, 0);
  };

  const toggleCategory = (cat) => {
    setCategory(prev => {
      if (prev.includes(cat)) {
        if (prev.length === 1) return prev; // 최소 하나 선택
        return prev.filter(c => c !== cat);
      }
      return [...prev, cat];
    });
  };

  const applyFormat = (item) => {
    const ta = textareaRef.current;
    const { selectionStart: s, selectionEnd: e } = ta;
    const inserted = item.wrap[0] + (content.slice(s, e) || "텍스트") + item.wrap[1];
    setContent(content.slice(0, s) + inserted + content.slice(e));
    setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = s + item.wrap[0].length + (content.slice(s, e) || "텍스트").length + item.wrap[1].length; }, 0);
  };

  const fieldStyle = { width:"100%", padding:"12px 16px", background:"#fff", border:`1px solid ${T.border}`, borderRadius:"10px", color:T.text, outline:"none", marginBottom:"20px", fontSize:"14px" };
  const labelStyle = { fontSize:"13px", fontWeight:700, color:T.text, textTransform:"uppercase", letterSpacing:"0.1em", display:"block", marginBottom:"8px" };

  if (authLoading) return null;

  return (
    <div style={{ minHeight:"100vh", background:T.bg, color:T.text, padding:"100px 24px" }}>
      <main style={{ maxWidth:"800px", margin:"0 auto" }}>
        <h1 style={{ fontSize:"1.8rem", fontWeight:800, marginBottom:"40px" }}>{isEditMode ? "프로젝트 수정" : "새 프로젝트 작성"}</h1>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom:"32px" }}>
            <label style={labelStyle}>카테고리 (중복 선택 가능)</label>
            <div style={{ display:"flex", gap:"12px" }}>
              {[
                { id: "dev", label: "개발" },
                { id: "security", label: "보안" }
              ].map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => toggleCategory(cat.id)}
                  style={{
                    flex: 1,
                    padding: "12px",
                    borderRadius: "10px",
                    border: `2px solid ${category.includes(cat.id) ? T.accent : T.border}`,
                    background: category.includes(cat.id) ? "rgba(14, 165, 233, 0.05)" : "#fff",
                    color: category.includes(cat.id) ? T.accent : T.textMuted,
                    fontWeight: 700,
                    fontSize: "13px",
                    cursor: "pointer",
                    transition: "all 0.2s"
                  }}
                >
                  {cat.label} {category.includes(cat.id) && "✓"}
                </button>
              ))}
            </div>
          </div>

          <label style={labelStyle}>제목</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="프로젝트 제목을 입력하세요" style={fieldStyle} />

          <label style={labelStyle}>진행 기간</label>
          <input value={period} onChange={e => setPeriod(e.target.value)} placeholder="예: 2024.03 ~ 진행 중" style={fieldStyle} />

          <label style={labelStyle}>요약 설명</label>
          <input value={summary} onChange={e => setSummary(e.target.value)} placeholder="카드에 노출될 짧은 설명을 입력하세요" style={fieldStyle} />

          <label style={labelStyle}>담당 역할</label>
          <input value={role} onChange={e => setRole(e.target.value)} placeholder="예: 백엔드 개발자" style={fieldStyle} />

          <TechTagInput tags={tech} onChange={setTech} />

          <div style={{ display:"flex", gap:"16px" }}>
            <div style={{ flex:1 }}>
              <label style={labelStyle}>GitHub 주소</label>
              <input value={github} onChange={e => setGithub(e.target.value)} placeholder="https://github.com/..." style={fieldStyle} />
            </div>
            <div style={{ flex:1 }}>
              <label style={labelStyle}>데모 링크 (선택)</label>
              <input value={demo} onChange={e => setDemo(e.target.value)} placeholder="https://..." style={fieldStyle} />
            </div>
          </div>

          {/* Media */}
          <div style={{ marginBottom:"32px" }}>
            <label style={labelStyle}>이미지 업로드</label>
            <div style={{ padding:"40px", border:`2px dashed ${T.border}`, borderRadius:"12px", textAlign:"center", cursor:"pointer", background:"#fff" }} onClick={() => document.getElementById('f').click()}>
              <span style={{ color:T.textMuted, fontSize:"14px" }}>이미지를 클릭하여 업로드하거나 드래그하세요</span>
            </div>
            <p style={{ fontSize:"12px", color:T.textMuted, marginTop:"10px", lineHeight:"1.5" }}>
              • 업로드된 이미지를 <strong>클릭</strong>하면 대표 썸네일로 설정됩니다.<br/>
              • <strong>본문 삽입</strong> 버튼을 누르면 마크다운 에디터의 커서 위치에 이미지가 삽입됩니다.
            </p>
            <input id="f" type="file" multiple hidden onChange={handleUpload} />
            {uploadedImages.length > 0 && (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(140px, 1fr))", gap:"16px", marginTop:"24px" }}>
                {uploadedImages.map(img => (
                  <div key={img.key} style={{ position:"relative", borderRadius:"12px", overflow:"hidden", border:`2px solid ${thumbnailUrl === img.thumbnailUrl ? T.accent : T.border}`, background:"#fff", transition:"all 0.2s" }}>
                    <img src={img.thumbnailUrl} style={{ width:"100%", height:"80px", objectFit:"cover", cursor:"pointer" }} onClick={() => setThumbnailUrl(img.thumbnailUrl)} title="썸네일로 설정" />
                    {thumbnailUrl === img.thumbnailUrl && (
                      <div style={{ position:"absolute", top:"4px", right:"4px", background:T.accent, color:"#fff", width:"20px", height:"20px", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"12px", fontWeight:"bold" }}>✓</div>
                    )}
                    <div style={{ padding:"8px", display:"flex", gap:"4px" }}>
                      <button type="button" onClick={() => insertToContent(img.url)} style={{ flex:1, padding:"4px 0", fontSize:"10px", fontWeight:700, background:"#f1f5f9", border:"none", borderRadius:"4px", cursor:"pointer" }}>본문 삽입</button>
                      <button type="button" onClick={() => copyToClipboard(img.url)} style={{ flex:1, padding:"4px 0", fontSize:"10px", fontWeight:700, background:"#f1f5f9", border:"none", borderRadius:"4px", cursor:"pointer" }}>URL 복사</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Content */}
          <div style={{ marginBottom:"40px" }}>
            <label style={labelStyle}>내용 (마크다운)</label>
            <div style={{ display:"flex", gap:"4px", background:"#f1f5f9", padding:"8px", border:`1px solid ${T.border}`, borderRadius:"10px 10px 0 0" }}>
              {TOOLBAR.map(b => (
                <button key={b.label} type="button" onClick={() => !b.sep && applyFormat(b)} style={{ background:"#fff", border:`1px solid ${T.border}`, padding:"4px 8px", borderRadius:"4px", fontSize:"11px", cursor:b.sep?"default":"pointer" }}>{b.label}</button>
              ))}
            </div>
            <textarea ref={textareaRef} value={content} onChange={e => setContent(e.target.value)} rows={15} placeholder="상세 내용을 마크다운으로 작성하세요" style={{ width:"100%", padding:"16px", border:`1px solid ${T.border}`, borderRadius:"0 0 10px 10px", outline:"none", lineHeight:"1.6", borderTop:"none", fontSize:"15px" }} />
          </div>

          <div style={{ display:"flex", justifyContent:"flex-end", gap:"12px" }}>
            <button type="button" onClick={() => navigate("/projects")} style={{ padding:"12px 24px", borderRadius:"99px", background:"#fff", border:`1px solid ${T.border}`, cursor:"pointer" }}>취소</button>
            <button type="submit" disabled={submitting} style={{ padding:"12px 40px", borderRadius:"99px", background:T.accent, border:"none", color:"#fff", fontWeight:700, cursor:submitting?"not-allowed":"pointer" }}>{submitting ? "저장 중..." : "저장"}</button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default ProjectWritePage;
